import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import { battleKeys, type KeeperDto } from './battles';

// Server: coop/CoopLobbyDtos.java's LobbyMemberDto — one seat (or pending invite) in a lobby.
// `role` is "host"/"invited"/"guest" verbatim off battle_lobby_members.role. `companionId`/
// `companionName`/`level` are null for an "invited" row — the invitee hasn't picked a companion
// yet; CoopLobbyService#join is what fills them in.
export type LobbyMemberDto = {
  accountId: string; displayName: string; role: string;
  companionId: number | null; companionName: string | null; level: number | null;
};

// Server: coop/CoopLobbyDtos.java's LobbyDto — one lobby, in full (design spec
// 2026-09-12-coop-pve-design.md §3.3/§4.2). `mode` is always "coop" for anything the web client
// builds today ("duel" belongs to the live-PvP spec). `battleId` is null until `start` fills it
// in. `members` lists every row the lobby currently has — host first, then guests, then pending
// invites — which is why LobbyMemberDto's own companion fields are nullable.
export type LobbyDto = {
  id: number; mode: 'coop' | 'duel'; host: KeeperDto;
  activityCode: string | null; encounterCode: string | null; status: string; expiresAt: string;
  members: LobbyMemberDto[]; battleId: number | null;
};

// Server: coop/CoopLobbyController.MineResponse — GET /coop/lobbies/mine's composed body: the
// caller's own open lobby (host or guest), if any, plus every still-open lobby they have a
// pending invite to. Both useMyLobby/useInvites below read off this ONE query — a second network
// round trip for the same data would be pointless, same "one query, two selectors" idiom
// battles.ts's useActiveBattle-adjacent hooks don't need but arena.ts's profile/opponents/history
// trio (three DISTINCT endpoints) shows the alternative to: here it's one endpoint, so one query.
type MineResponse = { mine: LobbyDto | null; invites: LobbyDto[] };

export const coopKeys = {
  all: ['coop'] as const,
  mine: ['coop', 'mine'] as const,
};

export const getMine = () => apiFetch<MineResponse>('/coop/lobbies/mine');

function useMine(options?: { refetchInterval?: number | false; enabled?: boolean }) {
  return useQuery({
    queryKey: coopKeys.mine,
    queryFn: getMine,
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled,
  });
}

/** The caller's own open lobby (host or guest), or null. One of the two selectors over the shared
 *  /mine query (see MineResponse's own doc comment above). */
export function useMyLobby() {
  const query = useMine();
  return { ...query, data: query.data ? query.data.mine : query.data };
}

/** Every still-open lobby the caller has a pending invite to. The other selector over the shared
 *  /mine query. Defaults the resolved value to [] (never undefined) once the query has data, same
 *  "expected absent state, not a loading state" shape flags.ts's useFlag establishes. */
export function useInvites(options?: { enabled?: boolean }) {
  const query = useMine(options);
  return { ...query, data: query.data ? query.data.invites : query.data };
}

/**
 * One lobby by id, for a lobby-sheet screen that already knows which lobby it's showing (e.g. an
 * accepted invite, or the host's own). There is deliberately NO dedicated `GET
 * /coop/lobbies/{id}` route on the server (checked against CoopLobbyController — only `/mine`
 * reads a lobby at all) — every LobbyDto the caller can see already comes back in full on
 * `/mine`'s `mine`/`invites` fields, so this composes over that SAME query rather than inventing a
 * second endpoint the server doesn't have. `options.refetchInterval` is its own independent
 * observer on the shared query key (TanStack Query supports per-observer intervals on one key),
 * so a lobby sheet can poll for a newly-joined member without forcing that same interval onto
 * `useMyLobby`/`useInvites` callers elsewhere in the app.
 */
export function useLobby(id: number | undefined, options?: { refetchInterval?: number | false }) {
  const query = useMine(options);
  const lobby = id == null || !query.data
    ? null
    : [query.data.mine, ...query.data.invites].find((l) => l?.id === id) ?? null;
  return { ...query, data: lobby };
}

function invalidateCoop(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: coopKeys.all });
}

// POST /coop/lobbies {activityCode, encounterCode, companionId} — CoopLobbyController.create;
// host auto-joins. Refusals (undocumented here as a typed result — see this hook's own doc
// comment): 409 battle_active (carries activeBattleId), 409 lobby_open, 400 requirement_unmet
// (carries firstUnmet), 404 not_available (coop_pve flag off).
export type CreateLobbyInput = { activityCode: string; encounterCode: string; companionId: number };

export const createLobby = (input: CreateLobbyInput) =>
  apiFetch<LobbyDto>('/coop/lobbies', { method: 'POST', body: JSON.stringify(input) });

/**
 * NOT a typed-result unwrap the way battles.ts's useStartBattle is for its own battle_active/
 * requirement_unmet pair — this task's brief lists no such contract for the lobby mutations, and a
 * screen that wants to special-case one of the refusals above can still read it off
 * `mutation.error` (an ApiClientError) the same way ShopScreen.tsx already does for `not_found`.
 * Invalidates coopKeys.all: a fresh lobby changes what `/mine` now resolves to.
 */
export function useCreateLobby() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createLobby,
    onSuccess: () => invalidateCoop(queryClient),
  });
}

// POST /coop/lobbies/{id}/invites {accountId} — CoopLobbyController.invite. Refusals: 404
// not_friends/blocked, 409 lobby_full, 429 rate_limited (20/hour/account).
export type InviteInput = { lobbyId: number; accountId: string };

export const inviteToLobby = (input: InviteInput) =>
  apiFetch<LobbyDto>(`/coop/lobbies/${input.lobbyId}/invites`, {
    method: 'POST', body: JSON.stringify({ accountId: input.accountId }),
  });

/** Invalidates coopKeys.all — the lobby's own `members` (a pending "invited" row) just changed. */
export function useInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: inviteToLobby,
    onSuccess: () => invalidateCoop(queryClient),
  });
}

// POST /coop/lobbies/{id}/join {companionId} — CoopLobbyController.join. Refusals: 409
// battle_active, 400 requirement_unmet, 409 lobby_full, 410 lobby_expired.
export type JoinLobbyInput = { lobbyId: number; companionId: number };

export const joinLobby = (input: JoinLobbyInput) =>
  apiFetch<LobbyDto>(`/coop/lobbies/${input.lobbyId}/join`, {
    method: 'POST', body: JSON.stringify({ companionId: input.companionId }),
  });

/** Invalidates coopKeys.all — the invite the caller just accepted moves from `invites` to `mine`. */
export function useJoinLobby() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: joinLobby,
    onSuccess: () => invalidateCoop(queryClient),
  });
}

// POST /coop/lobbies/{id}/leave — CoopLobbyController.leave. Guest leaves / host cancels /
// invited declines. 204, no body — the lobby's own next GET /mine already shows the result.
export const leaveLobby = (lobbyId: number) =>
  apiFetch<null>(`/coop/lobbies/${lobbyId}/leave`, { method: 'POST' });

/** Invalidates coopKeys.all — `mine`/`invites` both just lost this row. */
export function useLeaveLobby() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: leaveLobby,
    onSuccess: () => invalidateCoop(queryClient),
  });
}

// POST /coop/lobbies/{id}/start — CoopLobbyController.start (host starts, >=2 members required —
// 409 lobby_not_ready otherwise). Returns the LobbyDto with `battleId` now filled in.
export const startLobby = (lobbyId: number) =>
  apiFetch<LobbyDto>(`/coop/lobbies/${lobbyId}/start`, { method: 'POST' });

/**
 * Controller decision 3 (task brief): invalidates BOTH coopKeys.all (the lobby is no longer open)
 * AND battleKeys.active (GET /battles/active now resolves to the just-started coop battle) — same
 * "a mutation that starts a battle must invalidate battleKeys.active" idiom battles.ts's own
 * useStartBattle and arena.ts's useAttack already establish.
 */
export function useStartLobby() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: startLobby,
    onSuccess: () => {
      invalidateCoop(queryClient);
      queryClient.invalidateQueries({ queryKey: battleKeys.active });
    },
  });
}
